/**
 * Coverage areas multi-select for HealthcareService form.
 *
 * Reads from / writes to a CoverageAreaFormData[] field on a Mantine
 * form. Renders a single MultiSelect of supported state codes; on
 * change, builds the array of CoverageAreaFormData entries while
 * preserving any already-resolved locationId/locationDisplay metadata
 * for states that remain selected.
 *
 * Resolving the canonical state-coverage `Location` references is the
 * action layer's job (via enrichCoverageAreasWithLocations); this
 * component only deals in state codes.
 *
 * @agentic connections
 */

import { MultiSelect, type ComboboxProps } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { useMemo } from "react";
import {
  getCoverageAreaStateOptions,
  isCoverageAreaStateCode,
  type CoverageAreaStateCode,
} from "@benchmark/core/constants";
import type { CoverageAreaFormData } from "@benchmark/core/types";

/** Props for the CoverageAreasSection component. @agentic connections */
type CoverageAreasSectionProps<T extends Record<string, unknown>> = {
  form: UseFormReturnType<T>;
  fieldPath: string;
  placeholder?: string;
  /** Forwarded to the inner MultiSelect (e.g. to pin dropdown position). */
  comboboxProps?: ComboboxProps;
};

/**
 * Multi-select control for picking the geographic states a service
 * covers. Backed by a CoverageAreaFormData[] field on the form.
 * @agentic connections
 */
export const CoverageAreasSection = <T extends Record<string, unknown>>({
  form,
  fieldPath,
  placeholder = "Select states this service covers",
  comboboxProps,
}: CoverageAreasSectionProps<T>) => {
  const options = useMemo(() => [...getCoverageAreaStateOptions()], []);

  const coverageAreas =
    (form.values[fieldPath] as CoverageAreaFormData[] | undefined) ?? [];
  const value = coverageAreas
    .map((area) => area.state)
    .filter(isCoverageAreaStateCode);

  const setValue = (next: string[]) => {
    const supported = next.filter(isCoverageAreaStateCode);
    const previousByState = new Map<
      CoverageAreaStateCode,
      CoverageAreaFormData
    >(coverageAreas.map((area) => [area.state, area]));

    const updated: CoverageAreaFormData[] = supported.map((state) => {
      const previous = previousByState.get(state);
      return previous ?? { state };
    });

    (
      form.setFieldValue as (
        path: string,
        value: CoverageAreaFormData[],
      ) => void
    )(fieldPath, updated);
  };

  return (
    <MultiSelect
      placeholder={placeholder}
      data={options}
      searchable
      clearable
      value={value}
      onChange={setValue}
      comboboxProps={comboboxProps}
    />
  );
};
